import { useRef } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { coupleMonogram, type CoupleNameParts } from '@vendorops/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useCouplePhoto } from '@/hooks/useCouplePhoto';
import CouplePhoto from '@/components/CouplePhoto';

/**
 * Upload, replace, and clear the couple's portrait.
 *
 * **Saves on its own, not with the dialog's Save button.** The photo goes to R2
 * through `POST /api/weddings/:id/photo` rather than riding along in the JSON
 * `PUT`, so pretending it were part of the form would mean holding a file in
 * component state until Save, then firing two requests that can disagree about
 * whether they succeeded. Uploading on pick is both simpler and the behaviour
 * the preview implies — the picture changes in front of the planner, so it has
 * already been saved. The helper text underneath says so rather than leaving it
 * to be inferred.
 *
 * The requests themselves live in `useCouplePhoto`, shared with the hub
 * header's portrait: this is the labelled form version of that one action, and
 * the two must not drift on what an upload tells the planner.
 *
 * This is the screen that owns **removing** the photo. Clearing it is a
 * destructive edit to the wedding, and the header portrait deliberately has no
 * way to do it.
 */
export default function CouplePhotoField({
  weddingId,
  couple,
  hasPhoto,
}: {
  weddingId: string;
  couple: CoupleNameParts;
  hasPhoto: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { pick, remove, version, busy, uploading } = useCouplePhoto(weddingId);

  return (
    <div className="space-y-3">
      <Label>Photo</Label>
      <div className="flex items-center gap-4">
        <div className="w-24 shrink-0">
          <CouplePhoto
            weddingId={weddingId}
            hasPhoto={hasPhoto}
            monogram={coupleMonogram(couple)}
            version={version}
            alt=""
          />
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ImagePlus className="size-4" />
              )}
              {hasPhoto ? 'Replace' : 'Add photo'}
            </Button>
            {hasPhoto && (
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={remove}>
                <Trash2 className="size-4" />
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Saved as soon as you pick one — the Save button below is for the rest of the form.
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          // Cleared so picking the *same* file again after a failure still
          // fires a change event.
          e.target.value = '';
        }}
      />
    </div>
  );
}
